import { NetworkStatus, useQuery } from '@apollo/client';
import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Placeholder, Text } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GetJournalEntriesQuery, GetJournalEntriesQueryVariables } from '../client/generated/graphql';
import { getJournalEntriesQuery } from '../client/queries/journalEntries';
import { BaseSpinner } from '../components/common/BaseSpinner';
import { FocusLeePlaceholderImage } from '../components/common/FocusLeePlaceholderImage';
import { JournalEntrySection } from '../components/journal/JournalEntrySection';
import { RatingEntrySection } from '../components/journal/RatingEntrySection';
import { JournalPageSkeleton } from '../components/journal/skeleton/JournalPageSkeleton';
import { floatingTabbarScrollBottomPad } from '../helpers/floatingTabbarLayout';
import { toDueDate } from '../helpers/datetime';
import { journalEntryPath, ratingEntryPath } from '../navigation/paths';

type JournalEntry = GetJournalEntriesQuery['journalEntries'][0];
type RatingJournalEntry = JournalEntry & { rating: number };
type TextJournalEntry = JournalEntry;

type TimelineItem =
    | { kind: 'text'; entry: TextJournalEntry }
    | { kind: 'rating'; entry: RatingJournalEntry };

type DayGroup = {
    key: string;
    label: string;
    items: TimelineItem[];
};

const isRatingJournalEntry = (entry: JournalEntry): entry is RatingJournalEntry =>
    entry.rating != null;

const formatDayLabel = (date: string) => toDueDate(dayjs(date), false);

const formatTimeLabel = (date: Date | string) => dayjs(date).format('HH:mm');

export const JournalPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const pageSize = 10;
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const { data, networkStatus, fetchMore } = useQuery<GetJournalEntriesQuery, GetJournalEntriesQueryVariables>(
        getJournalEntriesQuery,
        {
            variables: { take: pageSize, orderBy: 'descending' },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-first',
        }
    );

    useEffect(() => {
        if (data && !initialLoadComplete && networkStatus !== NetworkStatus.fetchMore) {
            const initialHasMore = data.journalEntries.length === pageSize;
            setHasMore(initialHasMore);
            setInitialLoadComplete(true);
        }
    }, [data, initialLoadComplete, networkStatus, pageSize]);

    const entries = useMemo(() => data?.journalEntries ?? [], [data?.journalEntries]);

    const timelineItems = useMemo<TimelineItem[]>(
        () =>
            entries.map((entry) =>
                isRatingJournalEntry(entry)
                    ? { kind: 'rating', entry }
                    : { kind: 'text', entry },
            ),
        [entries],
    );

    const dayGroups = useMemo<DayGroup[]>(() => {
        const groups: DayGroup[] = [];
        const indexByKey = new Map<string, number>();

        for (const item of timelineItems) {
            const key = dayjs(item.entry.createdAt).format('YYYY-MM-DD');
            const existingIndex = indexByKey.get(key);
            if (existingIndex == null) {
                indexByKey.set(key, groups.length);
                groups.push({
                    key,
                    label: formatDayLabel(item.entry.createdAt),
                    items: [item],
                });
            } else {
                groups[existingIndex].items.push(item);
            }
        }

        return groups;
    }, [timelineItems]);

    const handleLoadMore = useCallback(() => {
        if (entries.length === 0 || !hasMore || networkStatus === NetworkStatus.fetchMore || isLoadingMore) {
            return;
        }

        setIsLoadingMore(true);

        const lastEntry = entries[entries.length - 1];
        const startAtCreatedAt = lastEntry ? new Date(lastEntry.createdAt) : undefined;

        fetchMore({
            variables: {
                startAtCreatedAt,
                take: pageSize,
                orderBy: 'descending',
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                setIsLoadingMore(false);

                if (!fetchMoreResult || fetchMoreResult.journalEntries.length === 0) {
                    setHasMore(false);
                    return previousResult;
                }

                const newHasMore = fetchMoreResult.journalEntries.length === pageSize;
                setHasMore(newHasMore);

                return {
                    journalEntries: previousResult.journalEntries.concat(fetchMoreResult.journalEntries),
                };
            },
        }).catch(() => {
            setIsLoadingMore(false);
        });
    }, [entries, hasMore, networkStatus, fetchMore, pageSize, isLoadingMore]);

    useEffect(() => {
        if (!hasMore || networkStatus === NetworkStatus.loading || !loadMoreRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            (observed) => {
                const entry = observed[0];

                if (entry.isIntersecting && hasMore && networkStatus !== NetworkStatus.fetchMore && !isLoadingMore) {
                    handleLoadMore();
                }
            },
            {
                threshold: 0.5,
                rootMargin: '100px',
            },
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
            observer.disconnect();
        };
    }, [hasMore, networkStatus, handleLoadMore, isLoadingMore]);

    const handleTextEntryClick = (entry: TextJournalEntry) => {
        if (entry.ritualId) {
            const path = journalEntryPath.replace(':ritualId?', entry.ritualId).replace('/:journalEntryId?', `/${entry.id}`);
            navigate(path);
        }
    };

    const handleRatingEntryClick = (entry: RatingJournalEntry) => {
        if (entry.ritualId) {
            const path = ratingEntryPath.replace(':ritualId?', entry.ritualId).replace('/:ratingEntryId?', `/${entry.id}`);
            navigate(path);
        }
    };

    if (networkStatus === NetworkStatus.loading) {
        return <JournalPageSkeleton />;
    }

    return (
        <Box sx={{ height: '100%', minHeight: 0, position: 'relative', overflow: 'hidden', bgcolor: 'var(--tgui--secondary_bg_color)' }}>
            <Stack
                sx={{
                    position: 'absolute',
                    inset: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    padding: '4px 0',
                    paddingTop: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
                    paddingBottom: floatingTabbarScrollBottomPad,
                }}
            >
                {timelineItems.length === 0 ? (
                    <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
                        <Placeholder
                            header={t('journal.emptyTitle')}
                            description={t('journal.emptyDescription')}
                            style={{ padding: 0 }}
                        >
                            <FocusLeePlaceholderImage kind="journal" />
                        </Placeholder>
                    </Stack>
                ) : (
                    <>
                        {dayGroups.map((group) => (
                            <Box key={group.key}>
                                <Text
                                    style={{
                                        display: 'block',
                                        color: 'var(--tgui--hint_color)',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        margin: '14px 10px 8px',
                                    }}
                                >
                                    {group.label}
                                </Text>
                                <Box sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
                                    <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
                                        {group.items.map((item, index) =>
                                            item.kind === 'text' ? (
                                                <JournalEntrySection
                                                    key={`text-${item.entry.id}`}
                                                    entry={item.entry}
                                                    timeLabel={formatTimeLabel(item.entry.createdAt)}
                                                    showDivider={index > 0}
                                                    onClick={() => handleTextEntryClick(item.entry)}
                                                />
                                            ) : (
                                                <RatingEntrySection
                                                    key={`rating-${item.entry.id}`}
                                                    entry={item.entry}
                                                    timeLabel={formatTimeLabel(item.entry.createdAt)}
                                                    showDivider={index > 0}
                                                    onClick={() => handleRatingEntryClick(item.entry)}
                                                />
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        <Box ref={loadMoreRef} sx={{ padding: '16px', textAlign: 'center', minHeight: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {hasMore && (
                                networkStatus === NetworkStatus.fetchMore || isLoadingMore ? (
                                    <BaseSpinner size="m" />
                                ) : (
                                    <Text style={{
                                        fontSize: '14px',
                                        color: 'var(--tgui--hint_color)',
                                        cursor: 'pointer',
                                    }}>
                                        {t('journal.loadMore')}
                                    </Text>
                                )
                            )}
                        </Box>
                    </>
                )}
            </Stack>
        </Box>
    );
};
